// ABOUTME: Font size dropdown for rich text editor toolbar.
// ABOUTME: Provides preset font sizes with clear visual feedback.

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface FontSizeDropdownProps {
  value: string | undefined;
  onChange: (size: string) => void;
}

const FONT_SIZES = [
  { label: 'Small', value: '12px' },
  { label: 'Normal', value: '14px' },
  { label: 'Medium', value: '16px' },
  { label: 'Large', value: '18px' },
  { label: 'XL', value: '20px' },
  { label: '2XL', value: '24px' },
  { label: '3XL', value: '28px' },
  { label: '4XL', value: '32px' },
];

export function FontSizeDropdown({ value, onChange }: FontSizeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (size: string) => {
    onChange(size);
    setIsOpen(false);
  };

  const currentSize = value || '14px';
  const currentLabel = FONT_SIZES.find((s) => s.value === currentSize)?.label || 'Normal';

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`px-2 py-1 rounded-sm flex items-center gap-1 text-xs transition-colors min-w-[70px] justify-between cursor-pointer ${
          isOpen ? 'bg-primary-600 text-white' : 'hover:bg-surface-100 text-surface-600'
        }`}
        title="Font size"
      >
        <span>{currentLabel}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 py-1 rounded-sm bg-surface-0 border border-surface-200 shadow-dropdown z-50 min-w-[120px]">
          {FONT_SIZES.map((size) => (
            <button
              key={size.value}
              type="button"
              onClick={() => handleSelect(size.value)}
              className={`w-full px-3 py-1.5 text-left text-xs flex items-center justify-between transition-colors cursor-pointer ${
                currentSize === size.value
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-surface-600 hover:bg-surface-100'
              }`}
            >
              <span style={{ fontSize: size.value }}>{size.label}</span>
              {currentSize === size.value && <Check className="w-3 h-3" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
